/// <amd-module name="Analytics/Shared/BuildService/BuildAnalyticsDataService" />
import * as Resources from "Analytics/Shared/BuildService/Resources";
import { IAnalyticsDataService } from "Analytics/Shared/Common/Types";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { ITfsPageService } from "Tfs/Platform/Page";
import { Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { routeUrl } from "VSS/Platform/Location";

export class BuildAnalyticsDataService extends VssService implements IAnalyticsDataService {
    private static readonly _buildHubActionAnalytics: string =
        "ms.vss-pipelineanalytics-web.new-build-definition-pipeline-analytics-view-cardmetrics";
    private static readonly _buildHubRouteUrl: string = "ms.vss-build-web.pipelines-hub-route";

    private _buildClientContentPromise: Promise<void> | undefined;
    private _buildDefinitionName: string;

    public async fetchDefinitionName(definitionId: number): Promise<string> {
        if (!this._buildDefinitionName) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            if (tfsPageData && tfsPageData.project) {
                const project = tfsPageData.project.name;

                await this._getBuildClientContent();
                const buildRestClient = this.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
                const buildDefinitionInfo = await buildRestClient.getDefinition(project, definitionId);
                this._buildDefinitionName = buildDefinitionInfo.name;
            } else {
                throw new Error(Resources.FailedToFetchProjectDetailsErrorText);
            }
        }

        return this._buildDefinitionName;
    }

    public getDefinitionUrl(definitionId: number): string {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");

        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const project = tfsPageData.project.name;
            const url = routeUrl(this.pageContext, BuildAnalyticsDataService._buildHubRouteUrl, {
                project: project,
                definitionId: definitionId.toString()
            });
            return url;
        } else {
            throw new Error(Resources.FailedToFetchProjectDetailsErrorText);
        }
    }

    public getAnalyticsCardMetricsUrl(definitionId: number): string {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");

        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const project = tfsPageData.project.name;
            const url = routeUrl(this.pageContext, BuildAnalyticsDataService._buildHubRouteUrl, {
                project: project,
                definitionId: definitionId.toString(),
                view: BuildAnalyticsDataService._buildHubActionAnalytics
            });
            return url;
        } else {
            throw new Error(Resources.FailedToFetchProjectDetailsErrorText);
        }
    }

    private _getBuildClientContent(): Promise<void> {
        if (!this._buildClientContentPromise) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this._buildClientContentPromise = contributionService.getContributionAsync("ms.vss-build-web.common-client");
        }
        return this._buildClientContentPromise;
    }
}

Services.add("IBuildAnalyticsDataService", { serviceFactory: BuildAnalyticsDataService });
